import React, { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import AdminLayout from '../../components/Admin/AdminLayout'
import { AdminStatCardFlex } from './adminUi'
import { REJECT_REASONS, defaultFeatureAccessForRole, featureOptionsForRole } from '../../constants/featureAccess'
import { ModalClose } from '../../components/icons'

const EMAIL_TAG_LABEL = {
  work: 'Work',
  free: 'Free Email',
  unknown: 'Unknown Domain',
}

const PAGE_SIZE = 7

const VIEW_TABS = [
  { id: 'active', label: 'Active queue' },
  { id: 'rejected', label: 'Rejected' },
]

const ACTIVE_STATUS_OPTIONS = [
  { id: 'all', label: 'All active' },
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
]

const SORT_OPTIONS = [
  { id: 'created_at:desc', label: 'Newest first' },
  { id: 'created_at:asc', label: 'Oldest first' },
  { id: 'updated_at:desc', label: 'Recently reviewed' },
  { id: 'name:asc', label: 'Name A → Z' },
  { id: 'name:desc', label: 'Name Z → A' },
  { id: 'company:asc', label: 'Company A → Z' },
  { id: 'role:asc', label: 'Role A → Z' },
  { id: 'status:asc', label: 'Status A → Z' },
]

function splitIntoColumns(items, columns = 2) {
  const size = Math.ceil(items.length / columns)
  return Array.from({ length: columns }, (_, i) => items.slice(i * size, (i + 1) * size))
}

function FeatureAccessPicker({ options, featureAccess, onToggle }) {
  const [leftCol, rightCol] = splitIntoColumns(options, 2)

  const renderItem = (opt) => (
    <label key={opt.key} className="admin-permission-item">
      <input
        type="checkbox"
        checked={featureAccess[opt.key] !== false}
        onChange={() => onToggle(opt.key)}
      />
      <span>{opt.label}</span>
    </label>
  )

  return (
    <div className="admin-permission-grid">
      <div className="admin-permission-col">{leftCol.map(renderItem)}</div>
      <div className="admin-permission-col">{rightCol.map(renderItem)}</div>
    </div>
  )
}

function formatDateTime(d) {
  if (!d) return '—'
  const dt = new Date(d)
  return dt.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).replace(',', '')
}

function exportCsv(rows) {
  const header = ['Name', 'Email', 'Email Type', 'Company', 'Role', 'Status', 'Submitted']
  const lines = rows.map((r) => [
    r.display_name,
    r.work_email,
    r.email_tag,
    r.company_name || '',
    r.role,
    r.review_status,
    r.event_at || r.created_at,
  ].map((c) => `"${String(c || '').replace(/"/g, '""')}"`).join(','))
  const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'registrations.csv'
  a.click()
  URL.revokeObjectURL(url)
}

export default function AdminRegistration() {
  const [stats, setStats] = useState(null)
  const [rows, setRows] = useState([])
  const [counts, setCounts] = useState({ active: 0, pending: 0, approved: 0, rejected: 0 })
  const [loading, setLoading] = useState(false)
  const [view, setView] = useState('active')
  const [statusFilter, setStatusFilter] = useState('all')
  const [roleFilter, setRoleFilter] = useState('all')
  const [emailFilter, setEmailFilter] = useState('all')
  const [sort, setSort] = useState('created_at:desc')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [page, setPage] = useState(1)
  const [reviewItem, setReviewItem] = useState(null)
  const [featureAccess, setFeatureAccess] = useState({})
  const [rejectTarget, setRejectTarget] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [rejectNote, setRejectNote] = useState('')
  const [busy, setBusy] = useState(false)

  const [sortBy, sortDir] = sort.split(':')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await axios.get('/api/admin/registrations', {
        params: {
          view,
          status: view === 'active' ? statusFilter : 'rejected',
          search: search.trim() || undefined,
          sortBy,
          sortDir,
          role: roleFilter,
          emailTag: emailFilter,
        },
      })
      setStats(res.data.stats)
      setRows(res.data.items || res.data.registrations || [])
      setCounts(res.data.counts || { active: 0, pending: 0, approved: 0, rejected: 0 })
      window.dispatchEvent(new Event('rs-admin-badges-changed'))
    } catch {
      toast.error('Failed to load registrations')
    } finally {
      setLoading(false)
    }
  }, [view, statusFilter, search, sortBy, sortDir, roleFilter, emailFilter])

  useEffect(() => { load() }, [load])

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const pageItems = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => { setPage(1) }, [view, statusFilter, roleFilter, emailFilter, search, sort])

  const resetFilters = () => {
    setStatusFilter('all')
    setRoleFilter('all')
    setEmailFilter('all')
    setSort('created_at:desc')
    setSearchInput('')
    setSearch('')
  }

  const openReview = (item) => {
    setReviewItem(item)
    setFeatureAccess(item.feature_access || defaultFeatureAccessForRole(item.role))
  }

  const toggleFeature = (key) => {
    setFeatureAccess((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleApprove = async (item = reviewItem) => {
    if (!item) return
    setBusy(true)
    try {
      await axios.post(`/api/admin/registration-approval/${item.id}`, {
        decision: 'approved',
        featureAccess,
      })
      toast.success('Registration approved')
      setReviewItem(null)
      load()
    } catch {
      toast.error('Approval failed')
    } finally {
      setBusy(false)
    }
  }

  const savePermissions = async () => {
    if (!reviewItem) return
    setBusy(true)
    try {
      await axios.patch(`/api/admin/users/${reviewItem.id}/feature-access`, { featureAccess })
      toast.success('Permissions saved')
      load()
    } catch {
      toast.error('Failed to save permissions')
    } finally {
      setBusy(false)
    }
  }

  const quickApprove = async (item) => {
    setBusy(true)
    try {
      await axios.post(`/api/admin/registration-approval/${item.id}`, {
        decision: 'approved',
        featureAccess: item.feature_access || defaultFeatureAccessForRole(item.role),
      })
      toast.success('Registration approved')
      load()
    } catch {
      toast.error('Approval failed')
    } finally {
      setBusy(false)
    }
  }

  const confirmReject = async () => {
    if (!rejectTarget) return
    if (!rejectReason) {
      toast.error('Please select a rejection reason')
      return
    }
    const reasonMeta = REJECT_REASONS.find((r) => r.value === rejectReason)
    setBusy(true)
    try {
      await axios.post(`/api/admin/registration-approval/${rejectTarget.id}`, {
        decision: 'rejected',
        rejectReason: reasonMeta?.label || rejectReason,
        rejectNote: rejectNote.trim() || undefined,
      })
      toast.success('Registration rejected. Notification email sent.')
      setRejectTarget(null)
      setReviewItem(null)
      setRejectReason('')
      setRejectNote('')
      load()
    } catch {
      toast.error('Rejection failed')
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async (item) => {
    if (!window.confirm(`Permanently delete rejected registration for "${item.display_name || item.work_email}"? This cannot be undone.`)) return
    setBusy(true)
    try {
      await axios.delete(`/api/admin/registrations/${item.id}`)
      toast.success('Rejected registration deleted')
      if (reviewItem?.id === item.id) setReviewItem(null)
      load()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AdminLayout>
      <p style={{ margin: '0 0 16px', color: '#606266', fontSize: 13, lineHeight: 1.5 }}>
        Review new member registrations. Rejected applications are kept in a separate tab and can be permanently deleted.
      </p>

      <div className="admin-stats-row cols-4">
        <AdminStatCardFlex
          icon="clock"
          cls="blue"
          label="Pending Review"
          value={loading ? '…' : String(counts.pending ?? stats?.pendingReview ?? 0)}
          sub="Auto-timeout in 48h"
        />
        <AdminStatCardFlex
          icon="check"
          cls="green"
          label="Approved Today"
          value={stats?.approvedToday ?? 0}
          sub={stats?.approvedDeltaPct ? `${stats.approvedDeltaPct} vs yesterday` : 'No approvals yet today'}
        />
        <AdminStatCardFlex
          icon="moderation"
          cls="orange"
          label="Rejected"
          value={loading ? '…' : String(counts.rejected ?? 0)}
          sub={`Today: ${stats?.rejectedToday ?? 0}`}
        />
        <AdminStatCardFlex
          icon="email"
          cls="purple"
          label="Total Registered"
          value={stats?.totalRegistered ?? 0}
          sub={`This month: ${stats?.newThisMonth ?? 0}`}
        />
      </div>

      <div className="admin-period-bar" style={{ marginBottom: 16 }}>
        <div className="admin-period-tabs">
          {VIEW_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`admin-period-tab${view === tab.id ? ' active' : ''}`}
              onClick={() => {
                setView(tab.id)
                if (tab.id === 'rejected') setStatusFilter('all')
              }}
            >
              {tab.label}
              {tab.id === 'rejected' && counts.rejected > 0 ? ` (${counts.rejected})` : ''}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-table-card">
        <div className="admin-table-header">
          <span className="admin-table-title">
            {view === 'rejected' ? 'Rejected registrations' : 'Registration queue'}
          </span>
          <span style={{ fontSize: 12, color: '#909399' }}>
            {loading ? 'Loading…' : `${rows.length} record(s)`}
          </span>
        </div>
        <div className="admin-filter-row" style={{ flexWrap: 'wrap' }}>
          {view === 'active' ? (
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {ACTIVE_STATUS_OPTIONS.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
            </select>
          ) : null}
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="all">All Roles</option>
            <option value="GP">GP</option>
            <option value="LP">LP</option>
            <option value="Startup">Startup</option>
            <option value="ServiceProvider">Service Provider</option>
          </select>
          <select value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)}>
            <option value="all">All Email Types</option>
            <option value="work">Work Email</option>
            <option value="free">Free Email</option>
            <option value="unknown">Unknown Domain</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            {SORT_OPTIONS.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
          </select>
          <input
            type="search"
            placeholder="Search name, email, company, notes…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setSearch(searchInput) }}
            style={{ minWidth: 220 }}
          />
          <button type="button" className="admin-btn sm secondary" onClick={() => setSearch(searchInput)}>Search</button>
          <button type="button" className="admin-btn sm secondary" onClick={() => exportCsv(rows)}>Export CSV</button>
          <button type="button" className="admin-btn sm secondary" onClick={resetFilters}>Reset</button>
          <button type="button" className="admin-btn sm secondary" onClick={load}>Refresh</button>
        </div>
        <table>
          <thead>
            <tr>
              <th style={{ width: '28%' }}>Applicant</th>
              <th style={{ width: '22%' }}>Company</th>
              <th style={{ width: '10%' }}>Role</th>
              <th style={{ width: '18%' }}>Time &amp; Status</th>
              <th className="admin-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((item) => {
              const roleKey = (item.role || '').toLowerCase()
              const tag = item.email_tag || 'unknown'
              const tagLabel = EMAIL_TAG_LABEL[tag] || tag
              const isPending = item.review_status === 'pending'
              return (
                <tr key={item.id}>
                  <td>
                    <div className="admin-applicant-cell">
                      <span className="name">{item.display_name}</span>
                      <div className="email-row">
                        <span className="email">{item.work_email}</span>
                        <span className={`admin-email-tag ${tag}`}>
                          {tag === 'work' ? '✓ ' : tag === 'free' ? '✗ ' : ''}
                          {tagLabel}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="admin-company-cell">
                      <span className="name">{item.company_name || '— (Individual)'}</span>
                      <span className="type">{item.company_subtitle}</span>
                    </div>
                  </td>
                  <td><span className={`admin-role-badge ${roleKey}`}>{item.role}</span></td>
                  <td>
                    <div className="admin-time-status-cell">
                      <span className="time">{formatDateTime(item.event_at)}</span>
                      <span className={`admin-reg-status ${item.review_status}`}>
                        {item.review_status === 'pending' ? 'Pending' : item.review_status === 'approved' ? 'Approved' : 'Rejected'}
                      </span>
                    </div>
                  </td>
                  <td className="admin-td-actions">
                    <div className="admin-action-btns">
                      <button
                        type="button"
                        className="admin-btn admin-btn-sm admin-btn-ghost"
                        disabled={busy}
                        onClick={() => openReview(item)}
                      >
                        {isPending ? 'Review' : 'View'}
                      </button>
                      {isPending && view === 'active' && (
                        <>
                          <button
                            type="button"
                            className="admin-btn admin-btn-sm admin-btn-success"
                            disabled={busy}
                            onClick={() => quickApprove(item)}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="admin-btn admin-btn-sm admin-btn-danger-ghost"
                            disabled={busy}
                            onClick={() => {
                              setRejectTarget(item)
                              setRejectReason('')
                              setRejectNote('')
                            }}
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {view === 'rejected' && item.review_status === 'rejected' && (
                        <button
                          type="button"
                          className="admin-btn admin-btn-sm admin-btn-danger-ghost"
                          disabled={busy}
                          onClick={() => handleDelete(item)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
            {!loading && !pageItems.length && (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: 32, color: 'var(--admin-text-muted)' }}>
                  {view === 'rejected' ? 'No rejected registrations.' : 'No registrations match your filters.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="admin-pagination">
          <div className="admin-pagination-info">
            Showing {rows.length ? (page - 1) * PAGE_SIZE + 1 : 0}–{Math.min(page * PAGE_SIZE, rows.length)} of {rows.length} registrations
          </div>
          <div className="admin-pagination-controls">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((n) => (
              <button key={n} type="button" className={page === n ? 'active' : ''} onClick={() => setPage(n)}>{n}</button>
            ))}
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
          </div>
        </div>
      </div>

      <div className="admin-policy-box">
        <h3>Platform Email &amp; Security Policy</h3>
        <div className="admin-policy-grid">
          <div className="admin-policy-item accepted">
            <h4>Accepted — Work Email</h4>
            <ul>
              <li><strong>✓</strong> Corporate domain (e.g. <strong>sequoiacap.com</strong>, <strong>gic.com</strong>)</li>
              <li><strong>✓</strong> University / institution domain (e.g. <strong>stanford.edu</strong>)</li>
              <li><strong>✓</strong> Government domain (e.g. <strong>gov.sg</strong>, <strong>gov.hk</strong>)</li>
              <li><strong>✓</strong> Verified custom domain — admin must verify ownership</li>
            </ul>
          </div>
          <div className="admin-policy-item rejected">
            <h4>Rejected — Free Email</h4>
            <ul>
              <li><strong>✗</strong> Gmail / Google Workspace (personal)</li>
              <li><strong>✗</strong> Outlook / Hotmail / MSN</li>
              <li><strong>✗</strong> QQ Mail / 163 / 126</li>
              <li><strong>✗</strong> Yahoo / iCloud / ProtonMail</li>
              <li><strong>✗</strong> Any other free public email provider</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="admin-policy-box" style={{ marginTop: 16 }}>
        <h3>Login — Work Email 2FA Requirement</h3>
        <div className="admin-policy-grid">
          <div className="admin-policy-item twofa">
            <h4>Every Login Requires Email Code</h4>
            <ul>
              <li>User enters email + password on login page</li>
              <li>Platform sends a <strong>4-digit code</strong> to registered work email</li>
              <li>Code valid for <strong>5 minutes</strong>, single-use</li>
              <li>After <strong>3 wrong attempts</strong>, account locked for 30 min</li>
              <li>No code = no access, even with correct password</li>
            </ul>
          </div>
          <div className="admin-policy-item why">
            <h4>Why This Matters</h4>
            <ul>
              <li>Prevents unauthorized access — even if password is leaked</li>
              <li>Ensures work email ownership — confirms user represents the org</li>
              <li>Full audit trail — each login logged with timestamp</li>
              <li>Blocks shared/hijacked accounts — only inbox owner can log in</li>
              <li>Simple &amp; secure — no app download needed, works globally</li>
            </ul>
          </div>
        </div>
      </div>

      {reviewItem && (
        <div className="admin-modal-overlay show" role="presentation" onClick={() => !busy && setReviewItem(null)}>
          <div className="admin-modal admin-modal-wide" role="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Review: {reviewItem.display_name}</h3>
              <ModalClose onClick={() => !busy && setReviewItem(null)} />
            </div>
            <div className="admin-modal-body">
              <div className="admin-review-meta-grid">
                <div className="admin-review-meta-block">
                  <p><strong>Email:</strong> {reviewItem.work_email}</p>
                  <p><strong>Company:</strong> {reviewItem.company_name || '—'}</p>
                  <p><strong>Role:</strong> {reviewItem.role}</p>
                </div>
                <div className="admin-review-meta-block">
                  <p><strong>Status:</strong> {reviewItem.review_status}</p>
                  {reviewItem.rejection?.reason && (
                    <p><strong>Rejection reason:</strong> {reviewItem.rejection.reason}</p>
                  )}
                  {reviewItem.rejection?.note && (
                    <p><strong>Rejection note:</strong> {reviewItem.rejection.note}</p>
                  )}
                </div>
              </div>
              {reviewItem.review_status === 'pending' ? (
                <div className="field admin-permission-field">
                  <label className="admin-permission-field-label">Feature access — choose what this user can view</label>
                  <FeatureAccessPicker
                    options={featureOptionsForRole(reviewItem.role)}
                    featureAccess={featureAccess}
                    onToggle={toggleFeature}
                  />
                </div>
              ) : reviewItem.review_status === 'approved' ? (
                <div className="field admin-permission-field">
                  <label className="admin-permission-field-label">Feature access</label>
                  <FeatureAccessPicker
                    options={featureOptionsForRole(reviewItem.role)}
                    featureAccess={featureAccess}
                    onToggle={toggleFeature}
                  />
                </div>
              ) : null}
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn admin-btn-ghost" disabled={busy} onClick={() => setReviewItem(null)}>Close</button>
              {reviewItem.review_status === 'approved' && (
                <button type="button" className="admin-btn admin-btn-sm admin-btn-success" disabled={busy} onClick={savePermissions}>
                  Save Permissions
                </button>
              )}
              {reviewItem.review_status === 'pending' && (
                <>
                  <button
                    type="button"
                    className="admin-btn admin-btn-sm admin-btn-danger-ghost"
                    disabled={busy}
                    onClick={() => {
                      setRejectTarget(reviewItem)
                      setRejectReason('')
                      setRejectNote('')
                    }}
                  >
                    Reject
                  </button>
                  <button type="button" className="admin-btn admin-btn-sm admin-btn-success" disabled={busy} onClick={() => handleApprove()}>
                    Approve &amp; Grant Access
                  </button>
                </>
              )}
              {reviewItem.review_status === 'rejected' && (
                <button
                  type="button"
                  className="admin-btn admin-btn-sm"
                  style={{ background: 'var(--admin-danger)', color: '#fff' }}
                  disabled={busy}
                  onClick={() => handleDelete(reviewItem)}
                >
                  Delete permanently
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {rejectTarget && (
        <div className="admin-modal-overlay show" role="presentation" onClick={() => !busy && setRejectTarget(null)}>
          <div className="admin-modal admin-modal-scroll" role="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Reject Registration</h3>
              <ModalClose onClick={() => !busy && setRejectTarget(null)} />
            </div>
            <div className="admin-modal-body">
              <p style={{ fontSize: 12.5, color: 'var(--admin-text-sec)', marginBottom: 14 }}>
                Reject <strong>{rejectTarget.display_name}</strong>&apos;s registration. An email will be sent to <strong>{rejectTarget.work_email}</strong>.
              </p>
              <div className="field">
                <label htmlFor="rejectReason">Rejection reason *</label>
                <select id="rejectReason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}>
                  <option value="">Select a rejection reason...</option>
                  {REJECT_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="rejectNote">Additional note (optional, included in email)</label>
                <textarea
                  id="rejectNote"
                  rows={3}
                  value={rejectNote}
                  onChange={(e) => setRejectNote(e.target.value)}
                  placeholder="e.g. Please register again with your corporate email domain..."
                />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn admin-btn-ghost" disabled={busy} onClick={() => setRejectTarget(null)}>Cancel</button>
              <button
                type="button"
                className="admin-btn admin-btn-sm"
                style={{ background: 'var(--admin-danger)', color: '#fff' }}
                disabled={busy}
                onClick={confirmReject}
              >
                Confirm rejection and send email
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}
